import { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

export interface DateRange {
  startDate?: number;
  endDate?: number;
  label: string;
}

interface DateRangeFilterProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

const PRESET_RANGES: DateRange[] = [
  { label: 'All Time', startDate: undefined, endDate: undefined },
  { label: 'Today', startDate: getStartOfDay(0), endDate: Date.now() },
  { label: 'Last 7 days', startDate: getStartOfDay(7), endDate: Date.now() },
  { label: 'Last 30 days', startDate: getStartOfDay(30), endDate: Date.now() },
  { label: 'Last 90 days', startDate: getStartOfDay(90), endDate: Date.now() },
];

const CUSTOM_LABEL = 'Custom range';

function getStartOfDay(daysAgo: number): number {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function timestampToDateString(ts: number): string {
  const d = new Date(ts);
  return d.toISOString().slice(0, 10);
}

function dateStringToStartOfDay(dateStr: string): number {
  const d = new Date(dateStr + 'T00:00:00');
  return d.getTime();
}

function dateStringToEndOfDay(dateStr: string): number {
  const d = new Date(dateStr + 'T23:59:59.999');
  return d.getTime();
}

function formatDateRangeLabel(startDate: number, endDate: number): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function isCustomRange(value: DateRange): boolean {
  return value.label.includes('–') && value.startDate != null && value.endDate != null;
}

export function DateRangeFilter({ value, onChange }: DateRangeFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<'presets' | 'custom'>('presets');
  const [customStart, setCustomStart] = useState(() =>
    value.startDate ? timestampToDateString(value.startDate) : ''
  );
  const [customEnd, setCustomEnd] = useState(() =>
    value.endDate ? timestampToDateString(value.endDate) : ''
  );

  const handlePresetSelect = (range: DateRange) => {
    onChange(range);
    setIsOpen(false);
    setView('presets');
  };

  const handleCustomRangeClick = () => {
    setView('custom');
    if (value.startDate && value.endDate) {
      setCustomStart(timestampToDateString(value.startDate));
      setCustomEnd(timestampToDateString(value.endDate));
    } else if (!customStart && !customEnd) {
      const today = timestampToDateString(Date.now());
      setCustomStart(today);
      setCustomEnd(today);
    }
  };

  const handleCustomApply = () => {
    if (customStart && customEnd) {
      const startDate = dateStringToStartOfDay(customStart);
      const endDate = dateStringToEndOfDay(customEnd);
      if (startDate <= endDate) {
        const label = formatDateRangeLabel(startDate, endDate);
        onChange({ startDate, endDate, label });
        setIsOpen(false);
        setView('presets');
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setView('presets');
  };

  const canApplyCustom = customStart && customEnd && customStart <= customEnd;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="complaints-select-date-range"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Date range: ${value.label}`}
        className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] min-w-[140px] text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
      >
        <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <span className="truncate">{value.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={handleClose} />

          {/* Dropdown */}
          <div
            role="listbox"
            aria-label="Date range options"
            className="absolute right-0 sm:right-auto sm:left-0 top-full mt-1 z-20 w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-[80vh] overflow-y-auto"
          >
            {view === 'presets' ? (
              <>
                {PRESET_RANGES.map((range, index) => (
                  <button
                    key={range.label}
                    role="option"
                    aria-selected={value.label === range.label && !isCustomRange(value)}
                    onClick={() => handlePresetSelect(range)}
                    data-testid={`complaints-date-option-${['all', 'today', '7days', '30days', '90days'][index]}`}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                      value.label === range.label && !isCustomRange(value)
                        ? 'text-gray-900 bg-gray-100'
                        : 'text-gray-700'
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
                <button
                  role="option"
                  aria-selected={isCustomRange(value)}
                  onClick={handleCustomRangeClick}
                  data-testid="complaints-date-option-custom"
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 border-t border-gray-100 ${
                    isCustomRange(value) ? 'text-gray-900 bg-gray-100' : 'text-gray-700'
                  }`}
                >
                  {CUSTOM_LABEL}
                </button>
              </>
            ) : (
              <div className="p-3 space-y-3" data-testid="complaints-date-custom-picker">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Start date</label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    data-testid="complaints-date-input-start"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">End date</label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    data-testid="complaints-date-input-end"
                    min={customStart}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setView('presets')}
                    className="flex-1 px-3 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleCustomApply}
                    disabled={!canApplyCustom}
                    data-testid="complaints-date-btn-apply"
                    className="flex-1 px-3 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Export default range for initial state
export const DEFAULT_DATE_RANGE: DateRange = PRESET_RANGES[0]; // All Time

// URL param encoding for persistence
export const DATE_PRESET_KEYS = ['all', 'today', '7d', '30d', '90d', 'custom'] as const;
export type DatePresetKey = (typeof DATE_PRESET_KEYS)[number];

export function dateRangeToParams(range: DateRange): Record<string, string> {
  if (range.startDate == null && range.endDate == null) return { datePreset: 'all' };
  if (range.label === 'Today') return { datePreset: 'today' };
  if (range.label === 'Last 7 days') return { datePreset: '7d' };
  if (range.label === 'Last 30 days') return { datePreset: '30d' };
  if (range.label === 'Last 90 days') return { datePreset: '90d' };
  if (range.startDate != null && range.endDate != null && range.label.includes('–')) {
    return {
      datePreset: 'custom',
      dateStart: new Date(range.startDate).toISOString().slice(0, 10),
      dateEnd: new Date(range.endDate).toISOString().slice(0, 10),
    };
  }
  return { datePreset: 'all' };
}

export function paramsToDateRange(params: URLSearchParams): DateRange {
  const preset = params.get('datePreset');
  if (preset === 'today') return PRESET_RANGES[1];
  if (preset === '7d') return PRESET_RANGES[2];
  if (preset === '30d') return PRESET_RANGES[3];
  if (preset === '90d') return PRESET_RANGES[4];
  if (preset === 'custom') {
    const start = params.get('dateStart');
    const end = params.get('dateEnd');
    if (start && end) {
      const startDate = dateStringToStartOfDay(start);
      const endDate = dateStringToEndOfDay(end);
      if (startDate <= endDate) {
        return { startDate, endDate, label: formatDateRangeLabel(startDate, endDate) };
      }
    }
  }
  return DEFAULT_DATE_RANGE;
}
